import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Check, AlertCircle, Sparkles, Star, Ban, StickyNote } from 'lucide-react';
import { NotaProductoModal, useNotasRapidas } from '@renderer/components/pos/NotaProducto';
import type { PaqueteDetalle, PaqueteVista, ProductoVista } from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { useAjustesPermisos } from '@renderer/store/useAjustesPermisos';
import { puedeUsarCanal } from '@shared/permisos';
import { IPC_CHANNELS } from '@shared/constants';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { nuevaClaveIntento } from '@renderer/lib/claveIntento';
import { formatPrecio } from '@renderer/utils/formatters';

/** Sprint 44 — valor centinela para indicar "el cliente excluyó este renglón". */
const EXCLUIDO = -1;

interface Props {
  abierto: boolean;
  paquete: PaqueteVista | null;
  onClose: () => void;
  onConfirmar: (resultado: ResultadoPaqueteProductos) => void;
}

export interface ResultadoPaqueteProductos {
  paqueteId: number;
  nombre: string;
  precioFinal: number;
  paqueteProductos: Array<{
    paqueteProductoId: number;
    productoFinalId: number;
    productoNombre: string;
    posicion: number;
    /** Sprint 44. true cuando el cliente excluyó el renglón. */
    excluido?: boolean;
  }>;
  notasEspeciales?: string;
}

/**
 * Sprint 7.4. Modificador unificado de paquetes con sustitución 1:1 por producto.
 * Por cada renglón del paquete (PaqueteProducto):
 *   - el cliente puede dejar el producto default o elegir un sustituto.
 *   - el precio del paquete se ajusta:
 *       precioFinal = paquete.precioBase + Σ(precioEnPaquete[final] − precioEnPaquete[default])
 *     donde precioEnPaquete cae a `precio` cuando es null.
 */
export function ModificadorPaqueteProductos({
  abierto,
  paquete,
  onClose,
  onConfirmar,
}: Props) {
  const [detalle, setDetalle] = useState<PaqueteDetalle | null>(null);
  // Map<paqueteProductoId, productoFinalId>
  const [seleccion, setSeleccion] = useState<Map<number, number>>(new Map());
  const [notas, setNotas] = useState('');
  /** Sprint 138 — El modal de notas, compartido con el carrito. */
  const [anotando, setAnotando] = useState(false);
  /** Sprint 139 — Los botones se piden aquí, antes de que el modal se abra (ver abajo). */
  const gruposNotas = useNotasRapidas();
  /**
   * Sprint 60 — Sustitución en el momento.
   *
   * Cuando un renglón se queda sin NADA servible, el paquete entero queda
   * invendible: el cliente sentado, la carne lista y el sistema diciendo AGOTADO
   * por un refresco. En un restaurante eso se resuelve preguntando — "no hay Coca,
   * ¿le doy Seven Up?" —, así que aquí se puede elegir otro producto sobre la
   * marcha. El backend sólo lo acepta cuando el renglón está caído de verdad.
   */
  const [emergencias, setEmergencias] = useState<Map<number, ProductoVista>>(new Map());
  const [servibles, setServibles] = useState<ProductoVista[]>([]);
  /**
   * Sprint 79 — "Llegó más".
   *
   * Reporte de la dueña: se le acabó la picaña **a media venta de un paquete** y mandó por más;
   * el sistema le decía agotado y la reposición vivía en otra pantalla, en lenguaje de
   * ingredientes. Quien está atendiendo no debería tener que aprender que "Picaña" son dos cosas
   * en nuestro modelo. Aquí repone y sigue vendiendo, sin cerrar el pedido.
   */
  const [reponiendo, setReponiendo] = useState<{ productoId: number; nombre: string } | null>(null);
  const [cantidadRepuesta, setCantidadRepuesta] = useState('');
  const [avisoReposicion, setAvisoReposicion] = useState<string | null>(null);
  const [guardandoReposicion, setGuardandoReposicion] = useState(false);
  const [cargando, setCargando] = useState(false);
  const negocio = useNegocioStore((s) => s.negocio);
  const sesionRol = useSessionStore((s) => s.sesion?.rol);
  const ajustesPermisos = useAjustesPermisos((s) => s.ajustes);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  /**
   * Cargar existencia es afirmar que llegó mercancía, así que el botón sólo aparece para quien
   * tiene ese permiso. A los demás no se les enseña una puerta que el servidor les va a cerrar.
   */
  const puedeReponer = puedeUsarCanal(
    sesionRol,
    IPC_CHANNELS.INSUMOS_REPONER_POR_PRODUCTO,
    ajustesPermisos,
  );
  const locale = negocio?.locale ?? 'es-MX';

  useEffect(() => {
    if (!abierto || !paquete) return;
    setCargando(true);
    setNotas('');
    window.api.paquetes.obtener(paquete.id).then((res) => {
      if (res.ok && res.data) {
        setDetalle(res.data);
        const inicial = new Map<number, number>();
        for (const r of res.data.productos) {
          inicial.set(r.id, r.productoId); // default = producto base del renglón
        }
        setSeleccion(inicial);
      }
      setCargando(false);
    });
    setEmergencias(new Map());
    // Los que se pueden servir hoy, para ofrecerlos si un renglón se cae. Se piden
    // una sola vez al abrir: la lista no cambia mientras se arma un paquete.
    window.api.productos.listar().then((res) => {
      if (res.ok) {
        setServibles(
          res.data
            .filter((p) => p.habilitado && (p.stockEfectivo === undefined || p.stockEfectivo === null || p.stockEfectivo > 0))
            .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
        );
      }
    });
  }, [abierto, paquete]);

  const { precioFinal, deltaTotal, hayStockInsuficiente, resumen } = useMemo(() => {
    if (!detalle || !paquete) {
      return { precioFinal: 0, deltaTotal: 0, hayStockInsuficiente: false, resumen: [] };
    }
    let delta = 0;
    let sinStock = false;
    const items: Array<{
      etiquetaPosicion: number;
      nombreFinal: string;
      cantidad: number;
      unidad: string;
      esDefault: boolean;
      excluido: boolean;
      deltaRenglon: number;
    }> = [];
    for (const renglon of detalle.productos) {
      const finalId = seleccion.get(renglon.id) ?? renglon.productoId;
      const excluido = finalId === EXCLUIDO;
      const esDefault = !excluido && finalId === renglon.productoId;

      if (excluido) {
        // Sprint 44 — el renglón se omite: el paquete baja en
        // precioEnPaquete[default] * cantidad y no se descuenta stock.
        const deltaRenglon = -(renglon.productoPrecioEnPaquete * renglon.cantidad);
        delta += deltaRenglon;
        items.push({
          etiquetaPosicion: renglon.posicion,
          nombreFinal: 'Sin esto',
          cantidad: renglon.cantidad,
          unidad: renglon.unidad,
          esDefault: false,
          excluido: true,
          deltaRenglon,
        });
        continue;
      }

      const baseInfo = {
        productoId: renglon.productoId,
        productoNombre: renglon.productoNombre,
        productoPrecioEnPaquete: renglon.productoPrecioEnPaquete,
        stockOk: renglon.stockOkDefault,
      };
      const sus = renglon.sustitutos.find((s) => s.productoId === finalId);
      // Un producto elegido en el momento no está en la lista de sustitutos: su
      // precio se toma del catálogo.
      const emergencia = emergencias.get(renglon.id);
      const infoEmergencia =
        !esDefault && !sus && emergencia && emergencia.id === finalId
          ? {
              productoId: emergencia.id,
              productoNombre: emergencia.nombre,
              productoPrecioEnPaquete: emergencia.precioEnPaquete ?? emergencia.precio,
              stockOk: true,
            }
          : undefined;
      const finalInfo = esDefault ? baseInfo : (sus ?? infoEmergencia);
      if (!finalInfo) continue;
      if (esDefault && !baseInfo.stockOk) sinStock = true;
      if (!esDefault && sus && !sus.stockOk) sinStock = true;
      const deltaRenglon = esDefault
        ? 0
        : finalInfo.productoPrecioEnPaquete - baseInfo.productoPrecioEnPaquete;
      delta += deltaRenglon;
      items.push({
        etiquetaPosicion: renglon.posicion,
        nombreFinal: finalInfo.productoNombre,
        cantidad: renglon.cantidad,
        unidad: renglon.unidad,
        esDefault,
        excluido: false,
        deltaRenglon,
      });
    }
    const precioCalculado = paquete.precioBase + delta;
    return {
      precioFinal: Math.max(0, precioCalculado),
      deltaTotal: delta,
      hayStockInsuficiente: sinStock,
      resumen: items,
    };
  }, [detalle, paquete, seleccion, emergencias]);

  /*
    Sprint 159 — Un toque, una carga. Sin esto, dos toques registraron dos veces 10 kg de lomo y
    20 kg de picaña. El ref frena en el acto; la clave hace que el servidor ignore la repetida.

    Sprint 164 (INCIDENTE, 21/10/2026) — Van ANTES del `return null`. Estaban después: con la
    ventana cerrada React contaba menos piezas que con la ventana abierta, y al abrir cualquier
    paquete truena ("Algo salió mal", error #310). Ningún hook puede ir después de un return.
  */
  const reponiendoRef = useRef(false);
  const intentoReposicionRef = useRef<{ firma: string; clave: string } | null>(null);

  if (!abierto || !paquete) return null;

  const handleConfirmar = () => {
    if (!detalle) return;
    if (hayStockInsuficiente) return;
    const seleccionFinal = detalle.productos.map((r) => {
      const finalId = seleccion.get(r.id) ?? r.productoId;
      const excluido = finalId === EXCLUIDO;
      if (excluido) {
        return {
          paqueteProductoId: r.id,
          productoFinalId: r.productoId, // back-compat: dummy, backend ignora cuando excluido=true
          productoNombre: 'Sin esto',
          posicion: r.posicion,
          excluido: true,
        };
      }
      const esDefault = finalId === r.productoId;
      const sus = r.sustitutos.find((s) => s.productoId === finalId);
      const nombreFinal = esDefault ? r.productoNombre : sus?.productoNombre ?? '';
      return {
        paqueteProductoId: r.id,
        productoFinalId: finalId,
        productoNombre: nombreFinal,
        posicion: r.posicion,
      };
    });
    onConfirmar({
      paqueteId: paquete.id,
      nombre: paquete.nombre,
      precioFinal,
      paqueteProductos: seleccionFinal,
      notasEspeciales: notas || undefined,
    });
    onClose();
  };

  /**
   * Carga lo que llegó y vuelve a leer el paquete: si el renglón se destrabó, el botón de
   * confirmar se enciende solo y la venta sigue donde estaba.
   */
  const confirmarReposicion = async () => {
    if (!reponiendo || !paquete || reponiendoRef.current) return;
    const cantidad = Number(cantidadRepuesta.replace(',', '.'));
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      setAvisoReposicion('Escribe cuánto llegó.');
      return;
    }
    const firma = `${reponiendo.productoId}:${cantidad}`;
    if (intentoReposicionRef.current?.firma !== firma) {
      const clave = nuevaClaveIntento();
      intentoReposicionRef.current = clave ? { firma, clave } : null;
    }
    reponiendoRef.current = true;
    setGuardandoReposicion(true);
    const res = await window.api.insumos
      .reponerPorProducto({
        productoId: reponiendo.productoId,
        cantidad,
        claveIntento: intentoReposicionRef.current?.clave,
      })
      .finally(() => {
        reponiendoRef.current = false;
        setGuardandoReposicion(false);
      });
    if (res.ok) intentoReposicionRef.current = null;
    if (!res.ok) {
      setAvisoReposicion(traducirErrorBackend(res.error, res.codigo));
      return;
    }
    setAvisoReposicion(
      `✓ Cargado. "${res.data.insumoNombre}" queda en ${res.data.stockNuevo} ${res.data.unidad}.`,
    );
    setReponiendo(null);
    setCantidadRepuesta('');
    const recargado = await window.api.paquetes.obtener(paquete.id);
    if (recargado.ok && recargado.data) setDetalle(recargado.data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Personaliza tu paquete
            </h2>
            <p className="text-sm text-slate-700 font-medium mt-0.5">{paquete.nombre}</p>
            {paquete.descripcion && (
              <p className="text-xs text-slate-500">{paquete.descripcion}</p>
            )}
          </div>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          {cargando ? (
            <div className="text-center text-slate-400 py-8">Cargando configuración…</div>
          ) : !detalle || detalle.productos.length === 0 ? (
            <div className="text-center text-slate-400 py-8">
              Este paquete no tiene productos configurados.
            </div>
          ) : (
            <>
              <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-900">
                Cada renglón muestra el producto sugerido (
                <Star className="inline h-3 w-3 text-amber-500 fill-amber-500 mx-1" />
                ). Si hay sustitutos, el cliente puede cambiarlo y el precio se ajustará.
              </div>

              {detalle.productos
                .slice()
                .sort((a, b) => a.posicion - b.posicion)
                .map((renglon) => {
                  const finalId = seleccion.get(renglon.id) ?? renglon.productoId;
                  const haySustitutos = renglon.sustitutos.length > 0;
                  const opcionesExtra: string[] = [];
                  if (!haySustitutos) opcionesExtra.push('sin sustitutos');
                  else opcionesExtra.push('elige UNO');
                  /**
                   * Sprint 73 — El regalo se anuncia en el punto de venta.
                   *
                   * Es lo que el mesero tiene que poder decirle al cliente sin dudar: "los frijoles
                   * van incluidos, no se cobran". Y si se acabó y no hay sustituto, el "o pídelo
                   * sin" es la salida que baja el precio del paquete.
                   */
                  if (renglon.esRegalo) opcionesExtra.unshift('GRATIS');
                  if (renglon.excluible) opcionesExtra.push('o pídelo sin');
                  return (
                    <div key={renglon.id} className="mb-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-sm flex items-center gap-2">
                          <span className="rounded-full bg-primary text-white text-xs font-bold w-5 h-5 flex items-center justify-center">
                            {renglon.posicion}
                          </span>
                          Renglón {renglon.posicion}
                          <span className="text-xs text-slate-400 font-normal">
                            ({renglon.cantidad} {renglon.unidad} · {opcionesExtra.join(' · ')})
                          </span>
                        </h3>
                      </div>
                      {/*
                        Sprint 79 — El renglón que frena la venta ofrece la salida ahí mismo.
                        Antes había que abandonar el pedido, ir a Inventario, encontrar el
                        ingrediente y volver a empezar.
                      */}
                      {!renglon.stockOkDefault && puedeReponer && (
                        <button
                          type="button"
                          onClick={() => {
                            setReponiendo({
                              productoId: renglon.productoId,
                              nombre: renglon.productoNombre,
                            });
                            setCantidadRepuesta('');
                            setAvisoReposicion(null);
                          }}
                          className="mb-2 min-h-[44px] w-full rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
                        >
                          📦 Llegó más {renglon.productoNombre} — cargarlo y seguir vendiendo
                        </button>
                      )}
                      <div className="grid gap-2 sm:grid-cols-2">
                        <OpcionBoton
                          nombre={renglon.productoNombre}
                          precioEnPaquete={renglon.productoPrecioEnPaquete}
                          esDefault
                          elegido={finalId === renglon.productoId}
                          stockOk={renglon.stockOkDefault}
                          deltaRespectoBase={0}
                          monedaSimbolo={monedaSimbolo}
                          locale={locale}
                          onClick={() => {
                            if (!renglon.stockOkDefault) return;
                            const next = new Map(seleccion);
                            next.set(renglon.id, renglon.productoId);
                            setSeleccion(next);
                          }}
                        />
                        {renglon.sustitutos.map((sus) => {
                          const delta =
                            sus.productoPrecioEnPaquete - renglon.productoPrecioEnPaquete;
                          const crossUnidad =
                            !!sus.productoUnidadVenta &&
                            !!renglon.unidad &&
                            sus.productoUnidadVenta !== renglon.unidad;
                          return (
                            <OpcionBoton
                              key={sus.productoId}
                              nombre={sus.productoNombre}
                              precioEnPaquete={sus.productoPrecioEnPaquete}
                              esDefault={false}
                              elegido={finalId === sus.productoId}
                              stockOk={sus.stockOk}
                              deltaRespectoBase={delta}
                              monedaSimbolo={monedaSimbolo}
                              locale={locale}
                              etiquetaSecundaria={
                                crossUnidad ? `1 ${sus.productoUnidadVenta} en lugar de ${renglon.cantidad} ${renglon.unidad}` : undefined
                              }
                              onClick={() => {
                                if (!sus.stockOk) return;
                                const next = new Map(seleccion);
                                next.set(renglon.id, sus.productoId);
                                setSeleccion(next);
                              }}
                            />
                          );
                        })}
                        {renglon.excluible && (
                          <OpcionExcluir
                            elegido={finalId === EXCLUIDO}
                            precioRebaja={renglon.productoPrecioEnPaquete * renglon.cantidad}
                            monedaSimbolo={monedaSimbolo}
                            locale={locale}
                            onClick={() => {
                              const next = new Map(seleccion);
                              next.set(renglon.id, EXCLUIDO);
                              setSeleccion(next);
                            }}
                          />
                        )}
                      </div>

                      {/*
                        Sprint 60 — El renglón se quedó sin nada servible.
                        Antes esto dejaba el paquete entero invendible por un solo
                        ingrediente. Ahora se resuelve aquí, como en la vida real:
                        se sirve otra cosa, queda anotado en la comanda y en el
                        ticket, y el precio se ajusta si difiere.
                      */}
                      {!renglon.stockOkDefault &&
                        renglon.sustitutos.every((x) => !x.stockOk) && (
                          <div className="mt-2 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2">
                            <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-900">
                              <AlertCircle className="h-4 w-4" />
                              No hay nada de este renglón
                            </p>
                            <p className="mt-0.5 text-xs text-amber-900">
                              Elige qué servir en su lugar. Se anota en la comanda y en el ticket.
                            </p>
                            <select
                              className="input mt-2"
                              value={emergencias.get(renglon.id)?.id ?? ''}
                              onChange={(e) => {
                                const elegido = servibles.find((p) => p.id === Number(e.target.value));
                                const nextEmer = new Map(emergencias);
                                const nextSel = new Map(seleccion);
                                if (elegido) {
                                  nextEmer.set(renglon.id, elegido);
                                  nextSel.set(renglon.id, elegido.id);
                                } else {
                                  nextEmer.delete(renglon.id);
                                  nextSel.set(renglon.id, renglon.productoId);
                                }
                                setEmergencias(nextEmer);
                                setSeleccion(nextSel);
                              }}
                            >
                              <option value="">— Elige qué servir —</option>
                              {servibles.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.nombre}
                                  {p.stockEfectivo !== undefined && p.stockEfectivo !== null
                                    ? ` — ${p.stockEfectivo} disponibles`
                                    : ''}
                                  {` · ${formatPrecio(p.precioEnPaquete ?? p.precio, monedaSimbolo, locale)}`}
                                </option>
                              ))}
                            </select>
                            {renglon.excluible && (
                              <p className="mt-1 text-xs text-amber-800">
                                También puedes pedirlo sin esto, con el botón de arriba.
                              </p>
                            )}
                          </div>
                        )}
                    </div>
                  );
                })}

              {resumen.length > 0 && (
                <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
                    Tu paquete
                  </div>
                  <ul className="space-y-1 text-sm">
                    {resumen.map((r, i) => (
                      <li key={i} className="flex items-center justify-between">
                        <span className={r.excluido ? 'text-orange-700' : 'text-slate-600'}>
                          <span className="text-slate-400">#{r.etiquetaPosicion}:</span>{' '}
                          <span className={`font-medium ${r.excluido ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                            {r.excluido ? 'Sin esto' : r.nombreFinal}
                          </span>{' '}
                          <span className="text-xs text-slate-400">
                            ({r.cantidad} {r.unidad})
                          </span>
                          {!r.esDefault && !r.excluido && (
                            <span className="ml-1 text-xs text-amber-600">(sustituido)</span>
                          )}
                          {r.excluido && (
                            <span className="ml-1 text-xs text-orange-600">(excluido)</span>
                          )}
                        </span>
                        {r.deltaRenglon !== 0 && (
                          <span className="font-mono text-xs">
                            {r.deltaRenglon > 0 ? '+' : ''}
                            {formatPrecio(r.deltaRenglon, monedaSimbolo, locale)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/*
                Sprint 138 — La misma captura de nota que el carrito, con los mismos botones.

                Antes aquí había una caja de texto suelta: el término se escribía a mano en los
                paquetes y ni se podía capturar en el mostrador. Dos formas distintas de anotar lo
                mismo es una de las dos mal (doctrina §6.8), y la que sobraba era ésta.
              */}
              <div className="mt-4">
                <span className="label">¿Cómo lo quiere el cliente? (opcional)</span>
                <button
                  type="button"
                  onClick={() => setAnotando(true)}
                  className={`flex min-h-[44px] w-full items-center gap-2 rounded-lg border px-3 text-left ${
                    notas
                      ? 'border-orange-300 bg-orange-50 font-semibold text-orange-900'
                      : 'border-dashed border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <StickyNote className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">
                    {notas || 'Término, corte, preparación…'}
                  </span>
                </button>
              </div>
            </>
          )}
        </div>

        <footer className="border-t border-slate-200 bg-slate-50 px-6 py-4">
          {/*
            Sprint 79 — Cuánto llegó. Se teclea aquí mismo, sin salir del pedido.

            Es el único momento en que esto importa: el cliente está esperando y la carne acaba de
            llegar de la carnicería. Mandar a otra pantalla es, en la práctica, no permitirlo.
          */}
          {reponiendo && (
            <div className="mb-3 space-y-2 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3">
              <p className="text-sm font-semibold text-emerald-900">
                ¿Cuánto llegó de {reponiendo.nombre}?
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  autoFocus
                  className="input w-32"
                  inputMode="decimal"
                  value={cantidadRepuesta}
                  onChange={(e) => setCantidadRepuesta(e.target.value)}
                  placeholder="Ej: 5"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void confirmarReposicion();
                  }}
                />
                <button
                  type="button"
                  onClick={() => void confirmarReposicion()}
                  disabled={guardandoReposicion}
                  className="btn-primary min-h-[44px] bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                >
                  {guardandoReposicion ? 'Cargando…' : 'Cargarlo'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReponiendo(null);
                    setAvisoReposicion(null);
                  }}
                  className="btn-ghost"
                >
                  Ahora no
                </button>
              </div>
              <p className="text-xs text-emerald-800">
                Se registra a tu nombre en la bitácora del ingrediente, igual que la carga del día.
              </p>
            </div>
          )}
          {avisoReposicion && !reponiendo && (
            <div className="mb-3 rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {avisoReposicion}
            </div>
          )}
          {hayStockInsuficiente && (
            <div className="mb-3 flex items-center gap-2 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4" /> Una opción seleccionada no tiene stock suficiente.
              {puedeReponer && ' Si ya llegó más, cárgalo con el botón verde del renglón.'}
            </div>
          )}
          <div className="mb-3 flex items-center justify-between">
            <div className="text-xs text-slate-500">
              <div>
                Base: {formatPrecio(paquete.precioBase, monedaSimbolo, locale)}
                {deltaTotal !== 0 && (
                  <> · Ajuste: {formatPrecio(deltaTotal, monedaSimbolo, locale)}</>
                )}
              </div>
            </div>
            <span className="font-mono text-2xl font-bold text-primary">
              {formatPrecio(precioFinal, monedaSimbolo, locale)}
            </span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost flex-1">
              Cancelar
            </button>
            <button
              onClick={handleConfirmar}
              className="btn-primary flex-1"
              disabled={cargando || hayStockInsuficiente}
            >
              Agregar al pedido
            </button>
          </div>
        </footer>
      </div>

      {/*
        Sprint 139 — Los botones se cargan con el modificador, no al abrir el modal de notas.

        Cuando el modal los pedía él mismo, la respuesta llegaba con el operador ya tocando
        opciones: al cambiar la lista, el modal se re-sembraba y **borraba lo que acababa de
        elegir y escribir**. Un round-trip en el track web son 100-500 ms, suficiente. Y mientras
        tanto enseñaba el vocabulario de fábrica en vez del del negocio.
      */}
      <NotaProductoModal
        abierto={anotando}
        nombreProducto={paquete?.nombre ?? 'este paquete'}
        notaActual={notas}
        grupos={gruposNotas}
        onGuardar={setNotas}
        onCerrar={() => setAnotando(false)}
      />
    </div>
  );
}

function OpcionBoton({
  nombre,
  precioEnPaquete,
  esDefault,
  elegido,
  stockOk,
  deltaRespectoBase,
  monedaSimbolo,
  locale,
  etiquetaSecundaria,
  onClick,
}: {
  nombre: string;
  precioEnPaquete: number;
  esDefault: boolean;
  elegido: boolean;
  stockOk: boolean;
  deltaRespectoBase: number;
  monedaSimbolo: string;
  locale: string;
  etiquetaSecundaria?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!stockOk}
      className={`flex w-full items-center justify-between rounded-lg border-2 px-3 py-2 text-left transition ${
        elegido
          ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
          : 'border-slate-200 bg-white hover:border-slate-300'
      } ${!stockOk ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div
          className={`flex h-5 w-5 items-center justify-center rounded-full border-2 shrink-0 ${
            elegido ? 'border-primary bg-primary text-white' : 'border-slate-300 bg-white'
          }`}
        >
          {elegido && <Check className="h-3 w-3" />}
        </div>
        <div className="min-w-0">
          <div className="font-medium text-sm flex items-center gap-1 truncate">
            {nombre}
            {esDefault && (
              <Star
                className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0"
                aria-label="Opción sugerida"
              />
            )}
          </div>
          <div className="text-xs text-slate-500">
            En paquete: {formatPrecio(precioEnPaquete, monedaSimbolo, locale)}
            {!stockOk && ' · sin stock'}
          </div>
          {etiquetaSecundaria && (
            <div className="text-[11px] text-amber-700 mt-0.5">{etiquetaSecundaria}</div>
          )}
        </div>
      </div>
      <span className="font-mono text-sm font-semibold whitespace-nowrap">
        {esDefault
          ? 'incluido'
          : deltaRespectoBase > 0
          ? `+${formatPrecio(deltaRespectoBase, monedaSimbolo, locale)}`
          : deltaRespectoBase < 0
          ? formatPrecio(deltaRespectoBase, monedaSimbolo, locale)
          : 'incluido'}
      </span>
    </button>
  );
}

function OpcionExcluir({
  elegido,
  precioRebaja,
  monedaSimbolo,
  locale,
  onClick,
}: {
  elegido: boolean;
  precioRebaja: number;
  monedaSimbolo: string;
  locale: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-lg border-2 px-3 py-2 text-left transition cursor-pointer ${
        elegido
          ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-200'
          : 'border-dashed border-slate-300 bg-slate-50 hover:border-orange-400'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div
          className={`flex h-5 w-5 items-center justify-center rounded-full border-2 shrink-0 ${
            elegido ? 'border-orange-500 bg-orange-500 text-white' : 'border-slate-300 bg-white'
          }`}
        >
          {elegido && <Check className="h-3 w-3" />}
        </div>
        <div className="min-w-0">
          <div className="font-medium text-sm flex items-center gap-1 truncate text-slate-700">
            <Ban className="h-3 w-3 text-orange-500 shrink-0" />
            Sin esto
          </div>
          <div className="text-xs text-slate-500">El cliente no lo quiere</div>
        </div>
      </div>
      <span className="font-mono text-sm font-semibold whitespace-nowrap text-orange-700">
        −{formatPrecio(precioRebaja, monedaSimbolo, locale)}
      </span>
    </button>
  );
}
